/**
 * Server-side read models for SEO surfaces (metadata, sitemaps, JSON-LD).
 *
 * These read from the Elasticsearch-backed endpoints rather than resolving a
 * DRep's anchor URL over IPFS at request time. The IPFS path is slow, fails
 * often in production, and was the reason profile pages were titled with raw
 * bech32 identifiers instead of names.
 */

import { serverApiUrl } from './config';
import type { IndexedProposal } from './proposal';
import {
  normalizeDRep,
  objectValue,
  readHighlights,
  textValue,
  type DRepSearchHit,
  type DRepSeoRecord,
  type DRepStatus,
} from './drepRecord';

export type { DRepSearchHit, DRepSeoRecord, DRepStatus };

const ONE_HOUR = 60 * 60;

/**
 * Process-level TTL cache. The app runs as a long-lived Node server
 * (`next start`), so this survives between requests and keeps the 2.3 MB
 * directory fetch off the critical path of individual page renders.
 *
 * Bounded, because several key spaces here are chosen by the caller and
 * `[dRepName]` is a root catch-all: `GET /<anything>` reaches
 * `findDRepSeoRecord('<anything>')`, so an unbounded map would grow for the
 * life of the process as any crawler or scanner walks the 404 space. Map
 * preserves insertion order, so the oldest key is the first one iteration
 * yields — an approximate LRU is enough here.
 */
const MAX_MEMO_ENTRIES = 4096;

type CatalogCache = {
  memo: Map<string, { value: unknown; expires: number }>;
  pending: Map<string, Promise<unknown>>;
};

// Next bundles instrumentation and routes independently. A module-local map
// does not cross those boundaries; the Node process global does. Namespace by
// upstream so development reloads cannot reuse another network's records.
const processCache = globalThis as typeof globalThis & {
  __drepSeoCatalogV2?: Map<string, CatalogCache>;
};
const catalogs = (processCache.__drepSeoCatalogV2 ??= new Map<
  string,
  CatalogCache
>());
let cache = catalogs.get(serverApiUrl);
if (!cache) {
  cache = { memo: new Map(), pending: new Map() };
  catalogs.set(serverApiUrl, cache);
}
const { memo, pending } = cache;

function remember(key: string, value: unknown, ttlSeconds: number) {
  if (memo.size >= MAX_MEMO_ENTRIES) {
    const now = Date.now();
    // forEach rather than for..of: the tsconfig target predates iterators.
    const stale: string[] = [];
    memo.forEach((entry, candidate) => {
      if (entry.expires <= now) stale.push(candidate);
    });
    stale.forEach((candidate) => memo.delete(candidate));
    // Still full: everything is live, so evict from the oldest end.
    while (memo.size >= MAX_MEMO_ENTRIES) {
      const oldest = memo.keys().next();
      if (oldest.done) break;
      memo.delete(oldest.value);
    }
  }
  memo.delete(key);
  memo.set(key, { value, expires: Date.now() + ttlSeconds * 1000 });
  if (key === 'drep-directory') {
    // Refreshing the directory also invalidates cached matches and misses.
    memo.forEach((_, candidate) => {
      if (candidate.startsWith('drep:')) memo.delete(candidate);
    });
  }
}

async function cached<T>(
  key: string,
  ttlSeconds: number,
  load: () => Promise<T>,
  options?: { force?: boolean }
): Promise<T> {
  const hit = memo.get(key);
  if (!options?.force && hit && hit.expires > Date.now()) return hit.value as T;
  let request = pending.get(key) as Promise<T> | undefined;
  if (!request) {
    request = Promise.resolve()
      .then(load)
      .then((value) => {
        remember(key, value, ttlSeconds);
        return value;
      })
      .catch((error) => {
        if (hit) {
          // Keep stale data usable and avoid retrying on every request during
          // an outage. A forced warm-up can still retry before this expires.
          hit.expires = Date.now() + 30_000;
        }
        throw error;
      })
      .finally(() => pending.delete(key));
    pending.set(key, request);
  }
  // A visitor with stale data never waits for the upstream timeout. The
  // scheduled warmer uses force to await and report refresh failures.
  if (hit && !options?.force) {
    void request.catch(() => undefined);
    return hit.value as T;
  }
  return request;
}

const configuredTimeout = Number(process.env.SEO_API_TIMEOUT_MS);
const API_TIMEOUT_MS =
  Number.isSafeInteger(configuredTimeout) && configuredTimeout > 0
    ? Math.min(configuredTimeout, 60_000)
    : 10_000;

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${serverApiUrl}${path}`, {
    // This catalog owns freshness. Next's additional fetch cache could make a
    // forced refresh return stale data and rejects large directory responses.
    cache: 'no-store',
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(`SEO catalog: ${path} responded ${response.status}`);
  }
  return (await response.json()) as T;
}

/** Every DRep that has resolvable metadata. Used to build the sitemap. */
export async function fetchDRepDirectory(options?: {
  force?: boolean;
}): Promise<DRepSeoRecord[]> {
  return cached(
    'drep-directory',
    ONE_HOUR,
    async () => {
      const raw = await getJson<unknown>('/all/dreps');
      if (!Array.isArray(raw))
        throw new Error('Invalid DRep directory response');
      return raw
        .map((entry) => normalizeDRep(objectValue(entry)._source))
        .filter((record): record is DRepSeoRecord => record !== null);
    },
    options
  );
}

/**
 * Whether `needle` is one of this DRep's on-chain identifiers.
 *
 * Only `meta`-derived fields are eligible. The indexer builds a record from two
 * sources of very different trust: a `meta` block derived from the on-chain
 * credential, and the DRep's own metadata document splatted in beside it. Only
 * the former identifies anybody.
 *
 * `declaredName` used to be matched here too, which is how a DRep could take
 * over another's page simply by setting `dRepName` to their handle or bech32
 * id: the first such record in directory order won.
 */
function matchesOnChainId(record: DRepSeoRecord, needle: string): boolean {
  const lower = needle.toLowerCase();
  return [record.drepId, record.drepIdCip105, record.drepIdHex].some(
    (candidate) => candidate?.toLowerCase() === lower
  );
}

/**
 * Longest thing that could plausibly be a DRep identifier or a minted handle.
 * CIP-129 bech32 ids are 63 characters; hex ids 58; handles shorter.
 */
const MAX_IDENTIFIER_LENGTH = 128;

/**
 * Shape of a name that could have been minted as a handle.
 *
 * Mirrors the mint-side rule in `src/app/drep/mint/index.tsx`: alphanumerics,
 * hyphen and underscore, with spaces allowed after the first character. Spaces
 * are legitimate here — a handle arrives URL-encoded — which is why the old
 * "URL-safe" test was the wrong shape as well as the wrong question.
 *
 * This is a cheap pre-filter, not an ownership check. `[dRepName]` is a root
 * catch-all, so every unmatched URL on the domain reaches this module. Asking
 * the ledger about `/wp-login.php` would spend Blockfrost quota on scanner
 * traffic, and that quota is metered (the backend has a 402 handler).
 */
const MINTABLE_HANDLE = /^[a-zA-Z0-9-_][a-zA-Z0-9-_ ]*$/;

/**
 * A handle is an asset name, and Cardano caps those at 32 bytes, so nothing
 * longer can have been minted. Every character MINTABLE_HANDLE admits is one
 * UTF-8 byte, so a length check is an exact byte check here.
 */
const MAX_HANDLE_LENGTH = 32;

/**
 * Shape of an on-chain identifier: bech32 (`drep1…`, `drep_vkh1…`) or raw hex.
 * Only these are worth a fuzzy search, since search results are accepted on an
 * exact id match and nothing else.
 */
const ON_CHAIN_ID = /^(?:drep[a-z0-9_]*1[a-z0-9]{20,}|[0-9a-f]{56,58})$/i;

/**
 * A handle is an asset minted under DREP_POLICY whose asset name *is* the
 * handle, so the ledger enforces exactly one owner. The asset's
 * `onchain_metadata.drepId` names that owner.
 *
 * This binding exists nowhere in Elasticsearch — the indexer never reads the
 * policy's assets and the mapping has no handle field — so it cannot be
 * inferred from catalog data, only asked for. `/drep?name=` is the same
 * ownership lookup the profile page itself trusts.
 */
async function resolveHandleOwner(handle: string): Promise<string | null> {
  const payload = await getJson<unknown>(
    `/drep?name=${encodeURIComponent(handle)}`
  );
  const tokens = objectValue(payload).token;
  if (!Array.isArray(tokens)) return null;
  for (const token of tokens) {
    const owner = textValue(objectValue(token).drepId);
    if (owner) return owner;
  }
  return null;
}

async function directoryMatch(needle: string): Promise<DRepSeoRecord | null> {
  const directory = await fetchDRepDirectory();
  return directory.find((record) => matchesOnChainId(record, needle)) ?? null;
}

/**
 * Fallback for a DRep registered since the last directory refresh. The query is
 * fuzzy full-text, so a result is accepted only on an exact id match —
 * otherwise a near miss would attach the wrong person's name to a profile.
 */
async function searchMatch(needle: string): Promise<DRepSeoRecord | null> {
  const results = await getJson<unknown[]>(
    `/search?query=${encodeURIComponent(needle)}`
  );
  return (
    (Array.isArray(results) ? results : [])
      .map(normalizeDRep)
      .find(
        (record): record is DRepSeoRecord =>
          record !== null && matchesOnChainId(record, needle)
      ) ?? null
  );
}

/** Shortest query worth sending upstream. */
const MIN_SEARCH_LENGTH = 2;

/**
 * Full-text DRep search — the same indexed endpoint behind the navbar
 * autocomplete, which searches the metadata documents (bio, objectives,
 * motivations, qualifications, references) and returns ranked snippets, not
 * just name and id substrings.
 *
 * Upstream returns the top 10 and ignores paging parameters, so a search is a
 * single page of best matches by construction.
 *
 * Cached briefly and gated on length: `/dreps?q=` is a public URL, and the
 * memo is shared with the rest of the catalog, so unbounded per-query keys
 * would evict the read models this module exists to keep warm.
 */
export async function searchDReps(query: string): Promise<DRepSearchHit[]> {
  const needle = query.trim();
  if (needle.length < MIN_SEARCH_LENGTH) return [];

  return cached(`drep-search:${needle.toLowerCase()}`, 300, async () => {
    const results = await getJson<unknown[]>(
      `/search?query=${encodeURIComponent(needle)}`
    );
    return (Array.isArray(results) ? results : [])
      .map((entry) => {
        const record = normalizeDRep(entry);
        if (!record) return null;
        return { record, highlights: readHighlights(entry) };
      })
      .filter((hit): hit is DRepSearchHit => hit !== null);
  });
}

/**
 * Whether a path segment could name something that exists at all: a minted
 * handle, or an on-chain identifier.
 *
 * Exported so the `[dRepName]` route can apply the same gate before its own
 * upstream call. That route reaches `/api/drep?name=` directly, which resolves
 * handle ownership through Blockfrost, and it never caches a miss — so
 * `/.env`, `/wp-login.php` and the rest of the scanner traffic that lands on
 * the root catch-all each cost a metered lookup. The gate inside this module
 * only protected this module's own calls.
 */
export function couldExist(identifier: string): boolean {
  const needle = identifier?.trim();
  if (!needle || needle.length > MAX_IDENTIFIER_LENGTH) return false;
  return (
    (MINTABLE_HANDLE.test(needle) && needle.length <= MAX_HANDLE_LENGTH) ||
    ON_CHAIN_ID.test(needle)
  );
}

async function locate(needle: string): Promise<DRepSeoRecord | null> {
  // Each upstream failure is softened so a later step still gets its turn, but
  // it is also recorded. "Upstream is down" and "nobody registered this" are
  // different answers, and only the second one is worth caching: returning null
  // for the first wrote a fresh negative entry over a DRep who does exist, so
  // their profile served `robots: noindex` and no JSON-LD for the rest of the
  // hour. Throwing instead lets `cached` keep and extend the previous entry.
  let degraded = false;
  const soften = () => {
    degraded = true;
    return null;
  };

  // The directory is already in memory, so a miss here is free. A failure to
  // load it is not fatal: the fallbacks below still apply.
  const byId = await directoryMatch(needle).catch(soften);
  if (byId) return byId;

  // Not an id we hold. It may be a minted handle, in which case only the
  // ledger can say whose it is.
  if (MINTABLE_HANDLE.test(needle) && needle.length <= MAX_HANDLE_LENGTH) {
    const owner = await resolveHandleOwner(needle).catch(soften);
    if (owner) {
      const byOwner = await directoryMatch(owner).catch(soften);
      const resolved = byOwner ?? (await searchMatch(owner).catch(soften));
      if (resolved) return resolved;
    }
  }

  // Only an id shape is still worth a fuzzy query, for a DRep registered since
  // the last directory refresh. Whether the directory loaded or not, the match
  // above already came back empty, so there is nothing more to ask it.
  if (ON_CHAIN_ID.test(needle)) {
    const found = await searchMatch(needle).catch(soften);
    if (found) return found;
  }

  // A genuine "not registered" still returns null and is still cached, so the
  // root catch-all keeps its protection against scanners walking the 404 space.
  if (degraded) {
    // The identifier is deliberately not interpolated: it is request-supplied
    // and unvalidated at this point, so it does not belong in a log line.
    throw new Error('SEO catalog: upstream unavailable while resolving a DRep');
  }
  return null;
}

/**
 * Resolve a DRep by an on-chain identifier or a minted handle.
 *
 * Identifiers are matched against the in-process directory; handles are
 * resolved through the backend to an owning DRep id first, then looked up by
 * that verified id. A self-declared `dRepName` resolves to nothing.
 */
export async function findDRepSeoRecord(
  identifier: string
): Promise<DRepSeoRecord | null> {
  const needle = identifier?.trim();
  if (!needle || needle.length > MAX_IDENTIFIER_LENGTH) return null;

  // Through `cached` rather than a bare memo read, for the `pending` map:
  // `generateMetadata` and the page body each resolve the same identifier and
  // run concurrently, so a bare read let both miss and both call upstream.
  // Handle resolution is metered (Blockfrost), so that doubling had a cost.
  // Negative results are cached too, so a repeatedly requested unknown
  // identifier does not re-query upstream on every hit. The map is bounded.
  return cached(`drep:${needle.toLowerCase()}`, ONE_HOUR, () => locate(needle));
}

/** Every governance action known to the indexer, newest first. */
export async function fetchProposals(options?: {
  force?: boolean;
}): Promise<IndexedProposal[]> {
  return cached(
    'proposals',
    ONE_HOUR,
    async () => {
      const first = await getJson<{
        totalCount: number;
        items: IndexedProposal[];
      }>('/proposals/indexed?page=1&size=500');

      const items = first.items || [];
      const total = first.totalCount ?? items.length;

      // Guard against a future page-size cap on the endpoint.
      for (let page = 2; items.length < total && page <= 20; page += 1) {
        const next = await getJson<{ items: IndexedProposal[] }>(
          `/proposals/indexed?page=${page}&size=500`
        );
        if (!next.items?.length) break;
        items.push(...next.items);
      }
      return items;
    },
    options
  );
}

export async function findProposal(
  id: string
): Promise<IndexedProposal | null> {
  const needle = id?.trim().split('#')[0];
  if (!needle) return null;
  const proposals = await fetchProposals();
  return (
    proposals.find((proposal) => proposal.id?.split('#')[0] === needle) ?? null
  );
}

/* ------------------------------------------------------------------ *
 * Cross-links between the two corpora.
 *
 * The vote feeds and the proposal index identify governance actions
 * differently: votes carry `<txHash>#<index>`, while URLs use the bech32
 * `gov_action1…` form. Proposals expose both — `createdAt.tx` and
 * `createdAt.index` — so the two can be joined. Likewise a vote's
 * `voter.credential` is the DRep's hex id minus its two-character CIP-129
 * prefix. These maps are what let a proposal page link to the DReps who voted
 * on it and a profile link back to the proposals it voted on.
 * ------------------------------------------------------------------ */

export interface ProposalVote {
  vote: string;
  voter?: { role?: string; credential?: string; hasScript?: boolean } | null;
  createdAt?: { time?: string | null; epoch?: number | null } | null;
}

export interface DRepVote {
  govActionId: string;
  title?: string | null;
  voteType: string;
  epochNo?: number | null;
  time?: string | null;
  govActionType?: string | null;
}

/** Votes cast on one governance action, by every voter role. */
export async function fetchProposalVotes(id: string): Promise<ProposalVote[]> {
  const needle = id?.trim().split('#')[0];
  if (!needle) return [];
  return cached(`proposal-votes:${needle}`, ONE_HOUR, async () => {
    const raw = await getJson<ProposalVote[] | { items: ProposalVote[] }>(
      `/proposal/${encodeURIComponent(needle)}/votes?page=1&size=1000`
    );
    return Array.isArray(raw) ? raw : raw?.items || [];
  });
}

export interface DRepVotePage {
  items: DRepVote[];
  /**
   * Total votes upstream reports, which may exceed `items.length`.
   *
   * Only the first page is fetched, so a prolific DRep's `items` stops at the
   * page size. Reporting `items.length` as the count therefore told every such
   * DRep they had voted exactly 100 times. Falls back to `items.length` when
   * the endpoint omits a total, which is no worse than the old behaviour.
   */
  total: number;
}

/** The first page of votes cast by one DRep, addressed by hex id. */
export async function fetchDRepVotes(drepIdHex: string): Promise<DRepVotePage> {
  const needle = drepIdHex?.trim();
  if (!needle) return { items: [], total: 0 };
  return cached(`drep-votes:${needle}`, ONE_HOUR, async () => {
    const raw = await getJson<{ items: DRepVote[]; totalCount?: number }>(
      `/drep/${encodeURIComponent(needle)}/vote?page=1&size=100`
    );
    const items = raw?.items || [];
    return { items, total: raw?.totalCount ?? items.length };
  });
}

/** Hex credential (no CIP-129 prefix) → DRep record. */
export async function credentialIndex(): Promise<Map<string, DRepSeoRecord>> {
  const directory = await fetchDRepDirectory();
  const map = new Map<string, DRepSeoRecord>();
  for (const record of directory) {
    const hex = record.drepIdHex;
    // Strip the leading type byte ("22" key-hash, "23" script) that CIP-129
    // prepends; vote records carry the bare credential.
    if (hex) map.set(hex.length > 56 ? hex.slice(-56) : hex, record);
  }
  return map;
}

/** `<txHash>#<index>` → proposal. */
export async function proposalByGovActionId(): Promise<
  Map<string, IndexedProposal>
> {
  const proposals = await fetchProposals();
  const map = new Map<string, IndexedProposal>();
  for (const proposal of proposals) {
    const tx = proposal.createdAt?.tx;
    if (tx) map.set(`${tx}#${proposal.createdAt?.index ?? 0}`, proposal);
  }
  return map;
}
